import React, { useState } from 'react';
import { CalendarOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../i18n/TModules';
import { orbitType } from '../../styles';
import { formatSupportDate, formatSupportDateTime, formatSupportYearMonth } from '../supportFormat';
import {
  IncidentsMaintenanceBlock,
  RenewalReviewFormBlock,
  SupportAction,
  SupportBlockMeta,
  SupportTicketDetailBlock,
  SupportTicketListBlock,
  WeeklyUpdateBlock
} from '../types';
import {
  CompactTable,
  CtaRow,
  Intro,
  KeyValueRow,
  Panel,
  PanelBody,
  PanelHead,
  PanelSubtitle,
  PanelTitle,
  Pill,
  Stack,
  StatusText,
  SupportActionButton,
  SupportIcon,
  TitleCell,
  VisuallyHidden,
  noop,
  sp
} from './supportStyles';

const T = `${TOrbit}.support`;

const IconButton = styled.button`
  padding: 2px;
  border: none;
  background: transparent;
  color: ${sp.muted};
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
`;

const Actions: React.FC<{ actions: SupportAction[] }> = ({ actions }) => (
  <>
    {actions.map(action => (
      <SupportActionButton key={action.id} action={action} />
    ))}
  </>
);

const Accordion = styled.div`
  border: 1px solid ${sp.border};
  border-radius: 10px;
  background: ${sp.inset};
`;

const AccordionHead = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 35px;
  padding: 0 13px;
  border: none;
  background: transparent;
  color: ${sp.text};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 700;
  text-align: left;

  > :first-child {
    margin-right: 10px;
    font-size: 9px;
  }

  > :nth-child(2) {
    flex: 1 1 auto;
  }

  > :nth-child(3) {
    color: ${sp.muted};
    ${orbitType.helping}
  }
`;

const AccordionBody = styled.div`
  padding: 0 13px 12px;
  color: ${sp.muted};
  ${orbitType.containerBody}

  ul {
    margin: 0;
    padding-left: 52px;
  }

  li + li {
    margin-top: 4px;
  }
`;

const SecondaryButton = styled.button`
  align-self: flex-start;
  width: 222px;
  max-width: 100%;
  min-height: 37px;
  border: 1px solid ${sp.border};
  border-radius: 8px;
  background: ${sp.inset};
  color: ${sp.text};
  cursor: pointer;
  ${orbitType.containerBody}
  font-weight: 700;
`;

interface IAccordionProps {
  title: string;
  count?: number;
  testId?: string;
  children?: React.ReactNode;
}

const StaticAccordion: React.FC<IAccordionProps> = ({ title, count, testId, children }) => {
  const expanded = children !== undefined;
  return (
    <Accordion data-testid={testId}>
      <AccordionHead type="button" aria-expanded={expanded} onClick={noop}>
        {expanded ? <DownOutlined aria-hidden="true" /> : <RightOutlined aria-hidden="true" />}
        <span>{title}</span>
        {count === undefined ? null : <span>{count}</span>}
      </AccordionHead>
      {expanded ? <AccordionBody>{children}</AccordionBody> : null}
    </Accordion>
  );
};

export const WeeklyUpdate: React.FC<{ block: WeeklyUpdateBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  const headAction = block.actions.find(action => action.kind === 'link');
  const footActions = block.actions.filter(action => action.kind !== 'link');
  return (
    <Stack $gap={9}>
      <Intro $panel>{block.intro}</Intro>
      <Panel>
        <PanelHead>
          <div>
            <PanelTitle>{t(`${T}.weekly.heading`)}</PanelTitle>
            <PanelSubtitle>
              {t(`${T}.weekly.weekOf`, { date: formatSupportDate(block.week_of) })}
            </PanelSubtitle>
          </div>
          {headAction === undefined ? null : (
            <IconButton type="button" aria-label={headAction.label} onClick={noop}>
              <SupportIcon icon={headAction.icon} />
            </IconButton>
          )}
        </PanelHead>
        <PanelBody $gap={8}>
          <div>
            <KeyValueRow>
              <span>{t(`${T}.weekly.reportingDate`)}</span>
              <span>{formatSupportDate(block.reporting_date)}</span>
            </KeyValueRow>
            <KeyValueRow>
              <span>{t(`${T}.weekly.projectManager`)}</span>
              <span>{block.project_manager}</span>
            </KeyValueRow>
          </div>
          <StaticAccordion title={t(`${T}.weekly.summary`)}>{block.summary}</StaticAccordion>
          <StaticAccordion title={t(`${T}.weekly.highlights`)}>
            <ul>
              {block.highlights.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </StaticAccordion>
          <StaticAccordion
            title={t(`${T}.weekly.resolvedIssues`)}
            count={block.resolved_issues_count}
            testId="orbit-support-weekly-resolved"
          />
          <StaticAccordion
            title={t(`${T}.weekly.openWatchItems`)}
            count={block.open_watch_items_count}
            testId="orbit-support-weekly-watch"
          />
          {footActions.some(action => action.kind === 'primary') ? (
            <CtaRow>
              {footActions.map(action => (
                <SupportActionButton key={action.id} action={action} />
              ))}
            </CtaRow>
          ) : (
            footActions.map(action => (
              <SecondaryButton key={action.id} type="button" onClick={noop}>
                {action.label}
              </SecondaryButton>
            ))
          )}
        </PanelBody>
      </Panel>
    </Stack>
  );
};

const TableWrap = styled.div`
  overflow-x: auto;
  ${intelligenceScrollbar('x')}
`;

export const IncidentsMaintenance: React.FC<{ block: IncidentsMaintenanceBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  const { incidents, maintenance } = block;
  const zone = maintenance.timezone_label;
  return (
    <Stack $gap={9}>
      <Intro $panel>{block.intro}</Intro>
      <Panel>
        <PanelHead>
          <PanelTitle>{t(`${T}.incidents.heading`)}</PanelTitle>
          <Pill $color={sp.danger}>
            {t(`${T}.incidents.openCount`, { count: incidents.items.length })}
          </Pill>
        </PanelHead>
        <PanelBody $gap={6}>
          <TableWrap>
            <CompactTable>
              <VisuallyHidden>{t(`${T}.incidents.heading`)}</VisuallyHidden>
              <thead>
                <tr>
                  <th>{t(`${T}.incidents.id`)}</th>
                  <th>{t(`${T}.incidents.title`)}</th>
                  <th>{t(`${T}.incidents.severity`)}</th>
                  <th>{t(`${T}.incidents.status`)}</th>
                  <th>{t(`${T}.incidents.opened`)}</th>
                </tr>
              </thead>
              <tbody>
                {incidents.items.map(item => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <TitleCell title={item.title} />
                    <td>
                      <Pill $color={sp.warning}>{item.priority}</Pill>
                    </td>
                    <td>
                      <StatusText $status={item.status}>
                        {t(`${T}.status.${item.status}`)}
                      </StatusText>
                    </td>
                    <td>{formatSupportDateTime(item.opened_at)}</td>
                  </tr>
                ))}
              </tbody>
            </CompactTable>
          </TableWrap>
          <Actions actions={incidents.actions} />
        </PanelBody>
      </Panel>
      <Panel>
        <PanelHead>
          <PanelTitle>{t(`${T}.maintenance.heading`)}</PanelTitle>
          <Pill $color={sp.link}>
            {t(`${T}.maintenance.upcomingCount`, { count: maintenance.items.length })}
          </Pill>
        </PanelHead>
        <PanelBody $gap={6}>
          <TableWrap>
            <CompactTable>
              <VisuallyHidden>{t(`${T}.maintenance.heading`)}</VisuallyHidden>
              <thead>
                <tr>
                  <th>{t(`${T}.maintenance.id`)}</th>
                  <th>{t(`${T}.maintenance.title`)}</th>
                  <th>{t(`${T}.maintenance.start`, { zone })}</th>
                  <th>{t(`${T}.maintenance.end`, { zone })}</th>
                </tr>
              </thead>
              <tbody>
                {maintenance.items.map(item => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <TitleCell title={item.title} />
                    <td>{formatSupportDateTime(item.starts_at)}</td>
                    <td>{formatSupportDateTime(item.ends_at)}</td>
                  </tr>
                ))}
              </tbody>
            </CompactTable>
          </TableWrap>
          <Actions actions={maintenance.actions} />
        </PanelBody>
      </Panel>
    </Stack>
  );
};

export const SupportTicketList: React.FC<{ block: SupportTicketListBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  const heading = t(`${T}.tickets.heading`, { count: block.tickets.length });
  return (
    <Stack $gap={9}>
      <Intro $panel>{block.intro}</Intro>
      <Panel>
        <PanelHead>
          <PanelTitle>{heading}</PanelTitle>
        </PanelHead>
        <PanelBody $gap={6}>
          <TableWrap>
            <CompactTable>
              <VisuallyHidden>{heading}</VisuallyHidden>
              <thead>
                <tr>
                  <th>{t(`${T}.tickets.id`)}</th>
                  <th>{t(`${T}.tickets.title`)}</th>
                  <th>{t(`${T}.tickets.severity`)}</th>
                  <th>{t(`${T}.tickets.status`)}</th>
                  <th>{t(`${T}.tickets.opened`)}</th>
                </tr>
              </thead>
              <tbody>
                {block.tickets.map(ticket => (
                  <tr key={ticket.id}>
                    <td>{ticket.id}</td>
                    <TitleCell title={ticket.title} />
                    <td>
                      <Pill $color={sp.warning}>{ticket.priority}</Pill>
                    </td>
                    <td>
                      <StatusText $status={ticket.status}>
                        {t(`${T}.status.${ticket.status}`)}
                      </StatusText>
                    </td>
                    <td>{formatSupportDate(ticket.opened_at)}</td>
                  </tr>
                ))}
              </tbody>
            </CompactTable>
          </TableWrap>
          <Actions actions={block.actions} />
        </PanelBody>
      </Panel>
    </Stack>
  );
};

const DescriptionBlock = styled.div`
  padding-top: 6px;
  color: ${sp.muted};
  ${orbitType.containerBody}

  p {
    margin: 4px 0 0;
  }
`;

export const SupportTicketDetail: React.FC<{
  block: SupportTicketDetailBlock & SupportBlockMeta;
}> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  const { ticket } = block;
  return (
    <Stack $gap={9}>
      <Intro $panel>{block.intro}</Intro>
      <Panel>
        <PanelBody $gap={0}>
          <KeyValueRow>
            <span>{t(`${T}.tickets.id`)}</span>
            <span>{ticket.id}</span>
          </KeyValueRow>
          <KeyValueRow>
            <span>{t(`${T}.tickets.title`)}</span>
            <span>{ticket.title}</span>
          </KeyValueRow>
          <KeyValueRow>
            <span>{t(`${T}.tickets.severity`)}</span>
            <span>
              <Pill $color={sp.warning}>{ticket.priority}</Pill>
            </span>
          </KeyValueRow>
          <KeyValueRow>
            <span>{t(`${T}.tickets.status`)}</span>
            <span>
              <StatusText $status={ticket.status}>{t(`${T}.status.${ticket.status}`)}</StatusText>
            </span>
          </KeyValueRow>
          <KeyValueRow>
            <span>{t(`${T}.tickets.created`)}</span>
            <span>{formatSupportDateTime(ticket.opened_at)}</span>
          </KeyValueRow>
          <KeyValueRow>
            <span>{t(`${T}.tickets.lastUpdated`)}</span>
            <span>{formatSupportDateTime(ticket.updated_at)}</span>
          </KeyValueRow>
          {block.jira_ref || block.state === 'jira_pending' ? (
            <KeyValueRow>
              <span>{t(`${T}.tickets.jira`)}</span>
              <span>{block.jira_ref ?? t(`${T}.tickets.jiraPending`)}</span>
            </KeyValueRow>
          ) : null}
          <DescriptionBlock>
            <span>{t(`${T}.tickets.description`)}</span>
            <p>{ticket.description}</p>
          </DescriptionBlock>
          {block.actions !== undefined && block.actions.length > 0 ? (
            <CtaRow>
              {block.actions.map(action => (
                <SupportActionButton key={action.id} action={action} />
              ))}
            </CtaRow>
          ) : null}
        </PanelBody>
      </Panel>
    </Stack>
  );
};

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${sp.muted};
  ${orbitType.helping}
`;

const MonthField = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid ${sp.border};
  border-radius: 8px;
  background: ${sp.inset};
  color: ${sp.text};
  cursor: pointer;
  ${orbitType.containerBody}
  text-align: left;

  > :first-child {
    margin-right: 10px;
    color: ${sp.muted};
    font-size: 16px;
  }
`;

const NotesField = styled.textarea`
  width: 100%;
  min-height: 60px;
  padding: 10px 12px;
  border: 1px solid ${sp.border};
  border-radius: 8px;
  background: ${sp.inset};
  color: ${sp.text};
  font: inherit;
  ${orbitType.containerBody}
  resize: vertical;

  &::placeholder {
    color: ${sp.muted};
  }
`;

const RenewalResult = styled.p<{ $error: boolean }>`
  margin: 0;
  padding: 10px 12px;
  border: 1px solid ${({ $error }) => ($error ? sp.danger : sp.success)};
  border-radius: 10px;
  background: ${({ $error }) => ($error ? sp.danger : sp.success)}24;
  color: ${sp.text};
  ${orbitType.containerBody}

  > strong {
    color: ${({ $error }) => ($error ? sp.danger : sp.success)};
    font-weight: 700;
  }
`;

let renewalFormCount = 0;

export const RenewalReviewForm: React.FC<{ block: RenewalReviewFormBlock }> = ({ block }) => {
  const { t } = useMultiLanguageTranslation();
  const [notes, setNotes] = useState('');
  const status = block.status ?? 'form';
  const locked = status !== 'form' && status !== 'failed';
  const [prefix] = useState(() => {
    renewalFormCount += 1;
    return `orbit-support-renewal-${renewalFormCount}`;
  });
  const ordered = [...block.actions].sort((first, second) =>
    first.kind === second.kind ? 0 : first.kind === 'secondary' ? -1 : 1
  );
  return (
    <Stack $gap={9}>
      <Intro $panel>{block.intro}</Intro>
      <Panel>
        <PanelHead>
          <PanelTitle>{t(`${T}.renewal.heading`)}</PanelTitle>
        </PanelHead>
        <PanelBody $gap={12}>
          <div>
            <KeyValueRow>
              <span>{t(`${T}.renewal.tenant`)}</span>
              <span>{block.tenant}</span>
            </KeyValueRow>
            <KeyValueRow>
              <span>{t(`${T}.renewal.contractEnd`)}</span>
              <span>{formatSupportDate(block.contract_end_date)}</span>
            </KeyValueRow>
            <KeyValueRow>
              <span>{t(`${T}.renewal.accountValue`)}</span>
              <span>{block.account_value}</span>
            </KeyValueRow>
          </div>
          <div>
            <FieldLabel htmlFor={`${prefix}-window`}>{t(`${T}.renewal.reviewWindow`)}</FieldLabel>
            <MonthField id={`${prefix}-window`} type="button" disabled={locked} onClick={noop}>
              <CalendarOutlined aria-hidden="true" />
              <span>{formatSupportYearMonth(block.default_review_window)}</span>
            </MonthField>
          </div>
          <div>
            <FieldLabel htmlFor={`${prefix}-notes`}>{t(`${T}.renewal.notes`)}</FieldLabel>
            <NotesField
              id={`${prefix}-notes`}
              rows={2}
              value={notes}
              readOnly={locked}
              placeholder={block.notes_placeholder}
              onChange={event => setNotes(event.target.value)}
            />
          </div>
          {status === 'submitted' ? (
            <RenewalResult role="status" $error={false}>
              <strong>{t(`${T}.renewal.submitted`)}</strong> {t(`${T}.renewal.submittedBody`)}
            </RenewalResult>
          ) : null}
          {status === 'failed' ? (
            <RenewalResult role="alert" $error>
              <strong>{t(`${T}.renewal.failed`)}</strong> {block.error ?? ''}
            </RenewalResult>
          ) : null}
          {status === 'submitted' ? null : (
            <CtaRow>
              {ordered.map(action => {
                if (status === 'submitting') {
                  return action.kind === 'primary' ? (
                    <SupportActionButton
                      key={action.id}
                      action={{ ...action, label: t(`${T}.renewal.submitting`) }}
                      disabled
                    />
                  ) : null;
                }
                return <SupportActionButton key={action.id} action={action} />;
              })}
            </CtaRow>
          )}
        </PanelBody>
      </Panel>
    </Stack>
  );
};
